import React, { useCallback, useState } from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { differenceInCalendarWeeks } from 'date-fns';
import Hyperlink from '@ingka/hyperlink';
import Text from '@ingka/text';
import Button from '@ingka/button';
import { mediaQueries } from '../styles/mediaQueries';
import { exists } from 'i18next';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import TimePicker from '../components/TimePicker';
import PageContent from '../components/PageContent';
import StorePicker from '../components/StorePicker';
import SiteMetadata from '../components/SiteMetadata';
import { AvailableService } from '../services/Appointment/model';
import Loading from '../components/Loading';
import useGetAvailableTimeslots from '../hooks/getAvailableTimeslots';
import RetryMessage from '../components/RetryMessage';
import { TemplateProps } from '../types/template';
import { services as apiService } from '../context';
import { showError } from '../reducers/error/errorReducer';
import { getLoginLink } from '../helpers/loginLinkHelper';
import {
  addPersistingQueryParamsToPath,
  getPath,
  handlePrev,
} from '../helpers/urlHelper';
import { useStateDispatch, useStateSelector } from '../store';
import { getSelectedTimeSlot } from '../helpers/timeSlotHelper';
import useAuthentication from '../hooks/useAuthentication';
import useInitPage from '../hooks/useInitPage';
import { startOfWeekDayToNumber } from '../helpers';
import { isPlainView } from '../helpers/plainViewHelper';
import { StyledWrapper } from '../components/PageLayout';
import useTranslation from '../hooks/useTranslation';
import translatePage from '../i18n/translatePage';
import { isNativeApp } from '../helpers/nativeAppHelper';
import StickyHeader from '../components/StickyHeader';
import StickyFooter from '../components/StickyFooter';
import SelectReplacementServiceConfirmation from '../components/SelectReplacementServiceConfirmation';
import {
  selectReplacementServiceSlots,
  selectSelectedTimeSlot,
  selectOriginalServiceSlots,
  selectSelectedOriginalService,
  selectServiceForTheSelectedTimeSlot,
} from '../reducers/stores/selectors';
import { useSelector } from 'react-redux';
/* global CSS_CONFIG */

const { loginRequired } = CSS_CONFIG.SERVICES;

interface StyledContainerProps {
  $isPlainView: boolean;
}

const SelectionContainer = styled.div<StyledContainerProps>`
  ${mediaQueries.portraitTabletUp} {
    margin: ${(props: StyledContainerProps) => props.$isPlainView && '0 4rem'};
  }
`;

const StyledParagraph = styled(Text)`
  &&& {
    margin-top: 0px;
    white-space: pre-line;
  }
`;

const TimeSlotsTemplate: React.FC<TemplateProps> = ({
  pageContext,
}): JSX.Element => {
  const { operation, view } = pageContext;
  const { t, i18n } = useTranslation();
  const dispatch = useStateDispatch();
  const { isAuthenticating } = useAuthentication();
  const { isInitPage } = useInitPage(operation, isAuthenticating, 'timeslots');
  const [reserveLoading, setReserveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const selectedOriginalTimeSlot = useStateSelector(
    (state) => state.app.selected.timeSlot,
  );
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const isLoading = useStateSelector((state) => state.app.isLoading);
  const searchPostCode = useStateSelector((state) => state.search.postCode);
  const slots = useStateSelector(selectOriginalServiceSlots);
  const replacementServiceSlots = useStateSelector(
    selectReplacementServiceSlots,
  );
  const stores = useStateSelector((state) => state.stores.availableStores);
  const from = useStateSelector((state) => state.timeSlots.from);
  const prevEnabled = useStateSelector((state) => state.timeSlots.prevEnabled);
  const selectedOriginalService = useStateSelector(
    selectSelectedOriginalService,
  );
  const selectedService = useSelector(selectServiceForTheSelectedTimeSlot);
  const isViewPlain = view === 'plain' || isPlainView();
  const isViewNative = view === 'native' || isNativeApp();

  const getNumberOfWeeksToShow = useCallback(
    (startDate: string): number => {
      const minWeeksToShowOnTheCalendar = 4;
      const maxWeeksToShowOnTheCalendar = 8;
      if (!slots || slots.length === 0) {
        return minWeeksToShowOnTheCalendar;
      }
      let firstWeekWithAvailableTimeSlots = 1;
      const firstDateWithTimeSlots = slots[0]?.date;
      firstWeekWithAvailableTimeSlots =
        firstWeekWithAvailableTimeSlots +
        differenceInCalendarWeeks(firstDateWithTimeSlots, startDate, {
          weekStartsOn: startOfWeekDayToNumber(
            CSS_CONFIG.FORMATS.firstDayOfWeek,
          ),
        });
      if (
        firstWeekWithAvailableTimeSlots <= minWeeksToShowOnTheCalendar ||
        firstWeekWithAvailableTimeSlots > maxWeeksToShowOnTheCalendar
      ) {
        return minWeeksToShowOnTheCalendar;
      }
      return firstWeekWithAvailableTimeSlots;
    },
    [slots],
  );

  const prebooked = getSelectedTimeSlot(
    slots,
    from,
    selectedOriginalTimeSlot?.id,
  );

  const [showRetry, onRetry] = useGetAvailableTimeslots(isInitPage);

  const isLoginRequired = loginRequired.some(
    (service) => !isLoggedIn && service === selectedService?.serviceId,
  );

  const handleLogin = () => {
    const currentLocation = window.location.href.replace(
      '/timeslots',
      '/contact',
    );
    window.location.href = getLoginLink(currentLocation, CSS_CONFIG);
  };

  const handleNextStep = () => {
    const path = getPath(operation, 'contact');
    return isLoginRequired
      ? handleLogin()
      : navigate(addPersistingQueryParamsToPath(path));
  };

  const reserveTimeSlot = async (): Promise<void> => {
    setReserveLoading(true);
    const { id = '' } = selectedTimeSlot || {};
    const { error } = await apiService.timeSlot.reserveTimeSlot(id);

    if (error) {
      setReserveLoading(false);
      dispatch(
        showError({
          title: t('pages.timeslots.reserveError.title'),
          description: t('pages.timeslots.reserveError.description'),
          button: t('pages.timeslots.reserveError.close'),
        }),
      );
      onRetry();
    } else {
      handleNextStep();
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (selectedOriginalTimeSlot) {
      await reserveTimeSlot();
      return;
    }
    setShowModal(true);
  };

  const handleContinueButton = async () => {
    await reserveTimeSlot();
  };

  const serviceHelpLink = () => {
    const text = t(
      `pages.timeslots.serviceHelpLink.${pageContext.operation}.text`,
    );
    if (
      exists &&
      exists(`pages.timeslots.serviceHelpLink.${pageContext.operation}.text`) &&
      text
    ) {
      return (
        <StyledParagraph tagName="p" textStyle="Body.L">
          <Trans
            i18n={i18n}
            i18nKey={text}
            components={[
              <Hyperlink
                url={t(
                  `pages.timeslots.serviceHelpLink.${pageContext.operation}.url`,
                )}
                newWindow
                rel="noopener noreferrer"
              >
                HELP LINK
              </Hyperlink>,
            ]}
          />
        </StyledParagraph>
      );
    }
    return null;
  };

  const timeslotsOrRetry = (service: AvailableService) =>
    showRetry ? (
      <RetryMessage
        message={t('pages.timeslots.errorMessage')}
        onRetry={onRetry}
      />
    ) : (
      <TimePicker
        weeks={getNumberOfWeeksToShow(from)}
        slots={slots}
        replacementServiceSlots={replacementServiceSlots}
        prebooked={prebooked}
        serviceId={serviceId}
        zipCode={searchPostCode}
        onTimespanChange={onRetry}
        prevEnabled={prevEnabled}
        from={from}
        location={service.location}
        isInStore={stores && stores.length > 0}
        operation={operation}
        disabled={reserveLoading}
      />
    );

  const getSubDescription = (): string => {
    const serviceSubDesc = t(
      `pages.timeslots.subDescriptionFallback.${serviceId}`,
    );

    return serviceSubDesc || t(`pages.timeslots.subDescription`);
  };

  const previousButtonIsVisible = disabledPrevButton !== 'timeslots';
  return (
    <>
      <SiteMetadata pageTitle={t('pages.timeslots.pageTitle')} />
      {view === 'plain' ? (
        <StickyHeader
          page="timeslots"
          isLoading={isInitPage || reserveLoading}
        />
      ) : (
        <AppHeader page="timeslots" />
      )}
      <form onSubmit={handleSubmit}>
        <StyledWrapper $withStickyFooter={isViewPlain || isViewNative}>
          <PageContent isLoading={isInitPage}>
            <Text tagName="h2" textStyle="Heading.L">
              {isViewPlain
                ? t('pages.timeslots.plainViewTitle')
                : t(`services.name.${serviceId}`)}
            </Text>
            {!isViewPlain && (
              <StyledParagraph tagName="p" textStyle="Body.L">
                {t(`services.description.${serviceId}`)}
              </StyledParagraph>
            )}
            {serviceHelpLink()}
            <SelectionContainer $isPlainView={isViewPlain}>
              {selectedOriginalService && stores && stores.length > 0 && (
                <StorePicker
                  headerText={t('pages.timeslots.storePickerTitle')}
                  serviceId={serviceId}
                  location={selectedOriginalService.location}
                  stores={stores}
                  operation={operation}
                  isDisabled={isLoading || reserveLoading}
                />
              )}
              {isLoading || !selectedOriginalService ? (
                <Loading title={t('components.loading.title.timeslots')} />
              ) : (
                <>
                  {timeslotsOrRetry(selectedOriginalService)}
                  <StyledParagraph tagName="p" textStyle="Body.L">
                    {getSubDescription()}
                  </StyledParagraph>
                </>
              )}
            </SelectionContainer>
            {isViewPlain || isViewNative ? (
              <StickyFooter>
                {isViewNative && previousButtonIsVisible && (
                  <Button
                    disabled={reserveLoading}
                    onClick={() => handlePrev(operation)}
                    text={t('components.stickyFooter.previousButton')}
                    type="secondary"
                    data-cy="previous"
                  />
                )}
                <Button
                  htmlType="submit"
                  id="nextButton"
                  disabled={!selectedTimeSlot || isLoading || isInitPage}
                  loading={reserveLoading}
                  text={t('components.stickyFooter.nextButton')}
                  type="emphasised"
                  aria-label={t(
                    'components.stickyFooter.ariaLabelToContactForm',
                  )}
                  data-cy="next-button"
                />
              </StickyFooter>
            ) : (
              <AppFooter
                prevVisible={previousButtonIsVisible}
                nextVisible
                isLoading={reserveLoading}
                nextDisabled={!selectedTimeSlot || isLoading || isInitPage}
                nextText={
                  isLoginRequired ? t('components.appFooter.login') : ''
                }
              />
            )}
            {selectedOriginalService &&
              selectedOriginalService.replacementService && (
                <SelectReplacementServiceConfirmation
                  onContinueButtonClick={handleContinueButton}
                  onCloseButtonClick={() => setShowModal(false)}
                  showModal={showModal}
                  selectedOriginalServiceInclSavingsPrice={
                    selectedOriginalService?.displayPrice?.inclSavings ?? 0
                  }
                  selectedOriginalServiceExclSavingsPrice={
                    selectedOriginalService?.displayPrice?.exclSavings ?? 0
                  }
                  replacementService={
                    selectedOriginalService.replacementService
                  }
                />
              )}
          </PageContent>
        </StyledWrapper>
      </form>
    </>
  );
};

export default translatePage()(TimeSlotsTemplate);
